import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from './use-prefers-reduced-motion.js';
import { SpeakingIndicator } from './speaking-indicator.js';
/** The data-saver hint, where the browser exposes it. */
function prefersReducedData() {
    const connection = navigator
        .connection;
    return connection?.saveData === true;
}
/** The listening loop, muted and decorative, revealed only once it actually
 * plays, so a slow load never covers the portrait with an empty circle. */
function ListeningLoop({ src, poster, visible, }) {
    const ref = useRef(null);
    const [playing, setPlaying] = useState(false);
    useEffect(() => {
        const video = ref.current;
        if (!video)
            return;
        if (visible) {
            // Autoplay of a muted inline video is allowed; a refusal simply
            // leaves the poster showing.
            void video.play()?.catch?.(() => { });
        }
        else {
            video.pause();
        }
    }, [visible]);
    const shown = visible && playing;
    return (_jsx("video", { ref: ref, "data-testid": "coach-listening-loop", "data-visible": shown ? 'true' : 'false', src: src, poster: poster, muted: true, loop: true, playsInline: true, preload: "auto", "aria-hidden": "true", tabIndex: -1, onPlaying: () => setPlaying(true), className: "sc-coach-listening-loop" }));
}
export function CoachOrb({ coach, size = 150, motion = null, speakingIndicator = false, inactive = false, listeningLoop = null, listening = false, style, ringColor, }) {
    const [failedPhoto, setFailedPhoto] = useState(undefined);
    const photo = coach.photo && coach.photo !== failedPhoto ? coach.photo : undefined;
    const accent = 'var(--color-accent)';
    // The outer ring's colour: the accent while live, a neutral grey while
    // not. The ripples spread from that ring, so they take the same colour.
    // The spinner's arc is the accent and the ring is its track: a pale accent
    // in the light theme and a subdued yellow in dark mode.
    const outline = ringColor ?? (motion === 'spinner'
        ? 'var(--color-coach-orb-pending-ring)'
        : inactive
            ? 'var(--color-coach-orb-idle-ring)'
            : accent);
    const reduceMotion = usePrefersReducedMotion();
    const loopSrc = listeningLoop && photo && !reduceMotion && !prefersReducedData() ? listeningLoop : null;
    // Remember the clip, not just whether any clip was armed, so changing
    // coaches while not listening does not preload the new coach's loop.
    const [armedSrc, setArmedSrc] = useState(null);
    useEffect(() => {
        if (listening && loopSrc)
            setArmedSrc(loopSrc);
    }, [listening, loopSrc]);
    // Every derived length scales off `size`, so a CSS-length size keeps the
    // same proportions through calc()/max() instead of JS arithmetic.
    const scaled = (factor) => typeof size === 'number' ? size * factor : `calc(${size} * ${factor})`;
    // The portrait's frame, as on the landing and login pages: an inner frame
    // (3% of the orb, never under 3px) and an accent ring outside it (1.5%,
    // never under 2px), both scaling so they hold on the large call frames.
    // The accent is the same for every coach and never mixed toward white: the
    // per-coach tints it replaced, mixed, came out a muted grey that read as the
    // not-live ring on a live call.
    const ringPad = typeof size === 'number'
        ? Math.max(3, size * 0.03)
        : `max(3px, calc(${size} * 0.03))`;
    const accentLine = typeof size === 'number'
        ? Math.max(2, size * 0.015)
        : `max(2px, calc(${size} * 0.015))`;
    const ringPadCss = typeof ringPad === 'number' ? `${ringPad}px` : ringPad;
    const accentLineCss = typeof accentLine === 'number' ? `${accentLine}px` : accentLine;
    return (_jsxs("div", { style: {
            position: 'relative',
            width: size,
            height: size,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto',
            ...style,
        }, children: [motion === 'ripples' &&
                [0, 1].map((i) => (_jsx("div", { "data-testid": "coach-orb-ring", 
                    // The class lets the reduced-motion rule stop the inline animation.
                    className: "sc-ring-pulse", "aria-hidden": true, style: {
                        position: 'absolute',
                        width: size,
                        height: size,
                        borderRadius: '50%',
                        // Separate declarations: a shorthand carrying max()/calc()
                        // is dropped by jsdom, which would also lose the colour.
                        borderStyle: 'solid',
                        // As thick as the outer ring they spread from.
                        borderWidth: accentLineCss,
                        borderColor: outline,
                        opacity: 0,
                        // Two rings half a cycle apart, so one is always on its way out.
                        animation: `sc-ring-pulse 2200ms ease-out ${i * 1100}ms infinite`,
                    } }, i))), _jsx("div", { "data-testid": "coach-orb-halo", "aria-hidden": true, style: {
                    position: 'absolute',
                    width: scaled(1.2),
                    height: scaled(1.2),
                    borderRadius: '50%',
                    background: `radial-gradient(circle, color-mix(in srgb, ${accent} 22%, transparent), transparent 68%)`,
                    opacity: inactive ? 0 : 1,
                    transition: 'opacity 320ms ease-out',
                } }), photo ? (_jsxs("div", { "data-testid": "coach-orb-photo", "data-inactive": inactive ? 'true' : undefined, style: {
                    position: 'relative',
                    width: size,
                    height: size,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    // The rings first: box-shadow paints the first layer on top, and the
                    // accent spill (cast downward, at about half the old reach)
                    // would otherwise tint the inner frame. Inactive, the outer ring is
                    // neutral and there is no spill.
                    boxShadow: inactive
                        ? `0 0 0 ${ringPadCss} var(--color-coach-orb-frame), 0 0 0 calc(${ringPadCss} + ${accentLineCss}) ${outline}`
                        : `0 0 0 ${ringPadCss} var(--color-coach-orb-frame), 0 0 0 calc(${ringPadCss} + ${accentLineCss}) ${outline}, 0 14px 30px -12px color-mix(in srgb, ${accent} 40%, transparent)`,
                    transition: 'box-shadow 320ms ease-out',
                }, children: [_jsx("img", { src: photo, alt: coach.name, onError: () => setFailedPhoto(photo), style: {
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                            // The not-live look sits on the picture alone, never on the
                            // rings or the frame: the avatar cover's scrim and
                            // desaturation, as a filter.
                            filter: inactive ? 'grayscale(0.3) brightness(0.86)' : undefined,
                            transition: 'filter 320ms ease-out',
                        } }), loopSrc && (armedSrc === loopSrc || listening) && (
                    // Keyed by the clip so a coach change while listening remounts the
                    // element: a fresh `playing` gate and a fresh play() call.
                    _jsx(ListeningLoop, { src: loopSrc, poster: photo, visible: listening }, loopSrc))] })) : (_jsx("div", { "data-testid": "coach-orb-fallback", "data-inactive": inactive ? 'true' : undefined, style: {
                    position: 'relative',
                    width: size,
                    height: size,
                    borderRadius: '50%',
                    // A flat accent disc, framed exactly like the portrait: the
                    // inner frame, the accent ring, the spill.
                    background: accent,
                    boxShadow: inactive
                        ? `0 0 0 ${ringPadCss} var(--color-coach-orb-frame), 0 0 0 calc(${ringPadCss} + ${accentLineCss}) ${outline}`
                        : `0 0 0 ${ringPadCss} var(--color-coach-orb-frame), 0 0 0 calc(${ringPadCss} + ${accentLineCss}) ${outline}, 0 14px 30px -12px color-mix(in srgb, ${accent} 40%, transparent)`,
                    filter: inactive ? 'grayscale(0.3) brightness(0.86)' : undefined,
                    transition: 'box-shadow 320ms ease-out, filter 320ms ease-out',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }, children: _jsx("span", { style: {
                        // Ink on the yellow disc, as on every accent fill.
                        color: 'var(--color-on-accent)',
                        fontSize: scaled(0.3),
                        fontWeight: 600,
                        letterSpacing: '-0.02em',
                    }, children: coach.initial }) })), speakingIndicator && motion === 'ripples' && (_jsx(SpeakingIndicator, { animated: true, "data-testid": "coach-orb-speaking", className: "sc-coach-orb-speaking" })), motion === 'spinner' && (
            // A quarter arc turning on the outer ring, which is its track. The
            // box reaches past the orb by the ring's distance, and a mask keeps
            // only the ring's own width, exact where a border would snap to whole
            // pixels and leave a sliver of the ring showing. After the portrait,
            // so it paints over that ring.
            _jsx("div", { "data-testid": "coach-orb-spinner", "aria-hidden": true, className: "sc-orb-spin", style: {
                    position: 'absolute',
                    inset: `calc(-1 * (${ringPadCss} + ${accentLineCss}))`,
                    borderRadius: '50%',
                    background: `conic-gradient(${accent} 0 25%, transparent 25%)`,
                    mask: `radial-gradient(farthest-side, transparent calc(100% - ${accentLineCss} - 0.5px), var(--color-black) calc(100% - ${accentLineCss}))`,
                } }))] }));
}
//# sourceMappingURL=coach-orb.js.map